import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Card } from '../ui/card';

export interface OrgChartPersonNodeData extends Record<string, unknown> {
  displayName: string;
  email: string;
  photoUrl?: string;
  organizationRole?: string;
  client?: string;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  // If true, this node receives connections from the left (horizontal branch)
  hasLeftConnection?: boolean;
}

const OrgChartPersonNode: React.FC<NodeProps> = ({ data, id }) => {
  const navigate = useNavigate();
  const nodeData = data as OrgChartPersonNodeData;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleClick = () => {
    navigate(`/people/${id}`);
  };

  return (
    <>
      {/* Top handle - for vertical connections from parent */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!opacity-0 !bg-transparent !border-0 !pointer-events-none !w-0 !h-0"
      />
      {/* Left handle - for horizontal branch connections from junction nodes */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!opacity-0 !bg-transparent !border-0 !pointer-events-none !w-0 !h-0"
      />
      <Card
        className={`p-3 min-w-[180px] w-[200px] cursor-pointer hover:shadow-lg transition-all duration-200 border-2 bg-white ${
          nodeData.isDimmed
            ? 'opacity-30 border-transparent'
            : 'border-transparent hover:border-prosigliere-blue'
        }`}
        onClick={handleClick}
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            {nodeData.photoUrl ? (
              <AvatarImage src={nodeData.photoUrl} alt={nodeData.displayName} />
            ) : null}
            <AvatarFallback className="bg-prosigliere-blue text-white text-sm font-medium">
              {getInitials(nodeData.displayName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm text-gray-900 truncate">
              {nodeData.displayName}
            </span>
            {nodeData.organizationRole && (
              <span className="text-xs text-gray-500 truncate">
                {nodeData.organizationRole}
              </span>
            )}
            {nodeData.client && (
              <span
                className="mt-1 w-fit max-w-full truncate rounded-full bg-prosigliere-blue/10 px-2 py-0.5 text-[10px] font-medium text-prosigliere-blue"
                data-testid="org-chart-node-client"
              >
                {nodeData.client}
              </span>
            )}
          </div>
        </div>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!opacity-0 !bg-transparent !border-0 !pointer-events-none !w-0 !h-0"
      />
    </>
  );
};

export default memo(OrgChartPersonNode);
